/**
 * Shared building blocks for the AI Clinical Copilot panel.
 *
 * The design rule from the spec is "reuse the existing HealNova Doctor Portal
 * design system" — so nothing here introduces a colour, radius or type scale.
 * Every surface is `card-surface` or a semantic token, headings are
 * `font-display`, and the AI labelling reuses `StatusBadge`'s tones rather than
 * inventing a second badge vocabulary.
 *
 * The one thing this file adds to the design system is a consistent way to say
 * "a machine suggested this" — because §3 requires AI output to be *clearly*
 * labelled, and that only works if it looks the same in all nine sections.
 */

import { ChevronDown, Sparkles, type LucideIcon } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { StatusBadge, type BadgeTone } from "@/components/common/status-badge";
import { cn } from "@/lib/utils";
import type { ItemSource, ItemStatus } from "@/types/copilot";

/**
 * The AI attribution mark.
 *
 * Deliberately unmissable and deliberately identical everywhere. A doctor
 * should never have to work out whether a line came from a person or a model.
 */
export function AiBadge({ className }: { className?: string | undefined }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-info/25 bg-info/10 px-2 py-0.5 text-[0.65rem] font-semibold tracking-wide text-info uppercase",
        className,
      )}
    >
      <Sparkles className="size-3" aria-hidden />
      AI suggestion
    </span>
  );
}

const STATUS_META: Record<ItemStatus, { label: string; tone: BadgeTone } | null> = {
  // A plain suggestion carries the AI badge instead — two marks would be noise.
  suggested: null,
  confirmed: { label: "Confirmed", tone: "success" },
  edited: { label: "Edited", tone: "brand" },
  rejected: { label: "Rejected", tone: "danger" },
  added: { label: "Added by you", tone: "brand" },
};

/** Renders whichever mark applies: the AI badge, or the doctor's decision. */
export function ProvenanceMark({ source, status }: { source: ItemSource; status: ItemStatus }) {
  const meta = STATUS_META[status];
  if (meta)
    return <StatusBadge label={meta.label} tone={meta.tone} className="py-0.5 text-[0.65rem]" />;
  if (source === "ai") return <AiBadge />;
  return null;
}

/**
 * One collapsible section of the panel.
 *
 * Collapsible because there are nine of them and a consultation only needs two
 * or three at a time; `defaultOpen` puts the live ones up front. The count in
 * the header is what makes a collapsed section still informative.
 */
export function CopilotSection({
  title,
  icon: Icon,
  count,
  badge,
  defaultOpen = false,
  children,
  action,
}: {
  title: string;
  icon: LucideIcon;
  count?: number | undefined;
  badge?: ReactNode;
  defaultOpen?: boolean | undefined;
  children: ReactNode;
  action?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <section className="border-b border-border last:border-b-0">
      <div className="flex items-center gap-2 px-4">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={contentId}
          className="flex flex-1 items-center gap-2.5 py-3 text-left transition-colors hover:text-primary"
        >
          <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="font-display text-sm font-semibold">{title}</span>
          {typeof count === "number" && count > 0 && (
            <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[0.65rem] font-semibold text-secondary-foreground">
              {count}
            </span>
          )}
          {badge}
          <ChevronDown
            className={cn(
              "ml-auto size-4 shrink-0 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
        {action && open && <div className="shrink-0">{action}</div>}
      </div>
      {open && (
        <div id={contentId} className="px-4 pb-4">
          {children}
        </div>
      )}
    </section>
  );
}

/** The empty state inside a section. Short, and never apologetic. */
export function SectionEmpty({ children }: { children: ReactNode }) {
  return <p className="py-2 text-xs leading-relaxed text-muted-foreground">{children}</p>;
}

/**
 * A section whose data source is not connected.
 *
 * Visually distinct from "no records" on purpose: §6 forbids fabricating
 * missing values, and an unconnected integration rendering as an empty result
 * is the same mistake one step removed.
 */
export function SectionUnavailable({ reason }: { reason?: string | undefined }) {
  return (
    <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
      {reason ?? "This data source is not connected yet."}
    </p>
  );
}

/** A field/value row, used by the vitals and lab tables. */
export function DataRow({
  label,
  value,
  meta,
  tone,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: "default" | "warning";
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-2 last:border-b-0">
      <div className="min-w-0">
        <p className="truncate text-xs font-medium">{label}</p>
        {meta && <p className="mt-0.5 text-[0.65rem] text-muted-foreground">{meta}</p>}
      </div>
      <p
        className={cn(
          "shrink-0 text-xs font-semibold tabular-nums",
          tone === "warning" ? "text-warning" : "text-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}
